import { useMemo, useState } from 'react';

export const useFilters = () => {
  const [sortBy, setSortBy] = useState('menu_order');
  const [filters, setFilters] = useState({
    vegetarian: false,
    vegan: false,
    glutenFree: false,
    dairyFree: false,
  });
  const [allergenFilters, setAllergenFilters] = useState({
    cereals_containing_gluten: [],
    crustaceans: [],
    eggs: [],
    fish: [],
    peanuts: [],
    soybeans: [],
    milk: [],
    nuts: [],
    celery: [],
    mustard: [],
    sesame_seeds: [],
    sulphur_dioxide_and_sulphites: [],
    lupin: [],
    molluscs: [],
  });

  const toggleNutrientFilter = (allergenKey, status) => {
    setAllergenFilters(prev => ({
      ...prev,
      [allergenKey]: prev[allergenKey].includes(status)
        ? prev[allergenKey].filter(s => s !== status)
        : [...prev[allergenKey], status],
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      vegetarian: false,
      vegan: false,
      glutenFree: false,
      dairyFree: false,
    });
    setAllergenFilters({
      cereals_containing_gluten: [],
      crustaceans: [],
      eggs: [],
      fish: [],
      peanuts: [],
      soybeans: [],
      milk: [],
      nuts: [],
      celery: [],
      mustard: [],
      sesame_seeds: [],
      sulphur_dioxide_and_sulphites: [],
      lupin: [],
      molluscs: [],
    });
  };

  const processMenuItems = useMemo(
    () =>
      (items, showLoadingPlaceholder = false, userPlan = 'free') => {
        if (!Array.isArray(items)) return [];

        let filteredItems = [...items];

        // Add loading placeholder if needed
        if (showLoadingPlaceholder) {
          const loadingItem = {
            loading: true,
            menuResponse: '🍽️ Loading menu items...',
            imageUrls: [],
            nutritional_score: null,
            approximate_calories: null,
            is_vegetarian: null,
            is_vegan: null,
            is_glutenFree: null,
            is_dairyFree: null,
          };

          if (filteredItems.length === 0) {
            filteredItems = Array(1)
              .fill(null)
              .map(() => ({ ...loadingItem }));
          } else {
            filteredItems = [...filteredItems, loadingItem];
          }
        }

        // Apply diet filters
        if (filters.vegetarian || filters.vegan || filters.glutenFree || filters.dairyFree) {
          filteredItems = filteredItems.filter(item => {
            if (item.loading) return true;

            return (
              (!filters.vegetarian || item.is_vegetarian === true) &&
              (!filters.vegan || item.is_vegan === true) &&
              (!filters.glutenFree || item.is_glutenFree === true) &&
              (!filters.dairyFree || item.is_dairyFree === true)
            );
          });
        }

        // Apply allergen filters
        const activeAllergenFilters = Object.entries(allergenFilters).filter(([_, statuses]) => statuses.length > 0);
        if (activeAllergenFilters.length > 0) {
          filteredItems = filteredItems.filter(item => {
            if (item.loading) return true;

            return activeAllergenFilters.every(([allergenKey, selectedStatuses]) => {
              const itemAllergenValue = item?.[allergenKey];

              if (!itemAllergenValue) {
                return true;
              }

              if (selectedStatuses.includes('high') && itemAllergenValue === 'high') {
                return false; // Filter out items with high risk for selected allergens
              }

              return true;
            });
          });
        }

        // Separate loading and regular items
        const loadingItems = filteredItems.filter(item => item.loading);
        const regularItems = filteredItems.filter(item => !item.loading);

        // Sort regular items
        regularItems.sort((a, b) => {
          switch (sortBy) {
            case 'nutri_score':
              return (b.nutritional_score || 0) - (a.nutritional_score || 0);
            case 'calories_low':
              return (a.approximate_calories || 0) - (b.approximate_calories || 0);
            case 'calories_high':
              return (b.approximate_calories || 0) - (a.approximate_calories || 0);
            case 'price_low':
              return (a.price || 0) - (b.price || 0);
            case 'price_high':
              return (b.price || 0) - (a.price || 0);
            case 'name':
              const nameA = typeof a === 'string' ? a : a.menuResponse || '';
              const nameB = typeof b === 'string' ? b : b.menuResponse || '';
              return nameA.localeCompare(nameB);
            default:
              return 0;
          }
        });

        // Free users can process any menu, but only the first five dishes are
        // unlocked. Section separators do not consume one of the previews.
        const FREE_PLAN_LIMIT = 5;
        let processedItems = [...regularItems];

        if (userPlan === 'free') {
          let visibleDishCount = 0;
          processedItems = regularItems.map(item => {
            const isSeparator = typeof item === 'object' && item.separator && !item.menuResponse;
            if (isSeparator) return item;

            visibleDishCount += 1;
            if (visibleDishCount > FREE_PLAN_LIMIT) {
              return {
                ...item,
                isLocked: true,
              };
            }
            return item;
          });
        }

        return [...processedItems, ...loadingItems];
      },
    [sortBy, filters, allergenFilters]
  );

  return {
    sortBy,
    setSortBy,
    filters,
    setFilters,
    allergenFilters,
    toggleNutrientFilter,
    clearAllFilters,
    processMenuItems,
  };
};
